import type { Permission, Role } from '@elutide/shared';
import type { IconName } from './icons';

export interface NavSection {
  id: string;
  label: string;
  /**
   * Name of a glyph in `components/icons.tsx`. The SVG is decorative and aria-hidden: the
   * label beside it carries the accessible name, so an unknown name costs nothing.
   */
  icon: IconName;
  href: string;
  badge?: number;
  /** The permission that makes this section reachable at all. */
  permission: Permission;
}

export interface NavBadges {
  inbox?: number;
  toPack?: number;
  criticalSkus?: number;
  needsReply?: number;
}

const SECTIONS: readonly (Omit<NavSection, 'badge'> & { roles: readonly Role[] })[] = [
  { id: 'home', label: 'Home', icon: 'home', href: '/', permission: 'home.read', roles: ['owner', 'packer', 'agent'] },
  { id: 'orders', label: 'Orders', icon: 'orders', href: '/orders', permission: 'orders.read', roles: ['owner', 'packer', 'agent'] },
  { id: 'stock', label: 'Stock', icon: 'stock', href: '/stock', permission: 'stock.read', roles: ['owner', 'packer'] },
  { id: 'bench', label: 'Bench', icon: 'bench', href: '/bench', permission: 'orders.print', roles: ['owner', 'packer'] },
  { id: 'customers', label: 'Customers', icon: 'customers', href: '/customers', permission: 'customers.read', roles: ['owner', 'agent'] },
  { id: 'support', label: 'Support', icon: 'support', href: '/support', permission: 'support.read', roles: ['owner', 'agent'] },
  { id: 'finance', label: 'Finance', icon: 'finance', href: '/finance', permission: 'finance.read', roles: ['owner'] },
  { id: 'growth', label: 'Growth', icon: 'growth', href: '/growth', permission: 'growth.read', roles: ['owner'] },
  { id: 'settings', label: 'Settings', icon: 'settings', href: '/settings', permission: 'settings.read', roles: ['owner'] },
];

/** The read permissions that also open a section, since a role may hold a narrower one. */
const ALTERNATES: Partial<Record<string, readonly Permission[]>> = {
  orders: ['orders.read', 'orders.read_packer', 'orders.read_agent'],
  stock: ['stock.read', 'stock.read_packer'],
  customers: ['customers.read', 'customers.read_masked'],
};

/**
 * The sections a role sees.
 *
 * This is convenience only. A route a role cannot reach is not rendered in the nav and
 * returns the not-authorized screen if it is typed directly, but the API is what enforces
 * it. Hiding a link has never stopped anyone.
 */
export function navSectionsFor(
  role: Role | null,
  badges: NavBadges = {},
  permissions?: ReadonlySet<Permission>,
): NavSection[] {
  if (!role) return [];
  const sections = SECTIONS.filter((section) => {
    if (!section.roles.includes(role)) return false;
    if (!permissions || permissions.size === 0) return true;
    const candidates = ALTERNATES[section.id] ?? [section.permission];
    return candidates.some((permission) => permissions.has(permission));
  }).map((section) => {
    const badge = badgeFor(section.id, badges);
    const { roles: _roles, ...rest } = section;
    return badge === undefined ? { ...rest } : { ...rest, badge };
  });
  return orderFor(role, sections);
}

/**
 * The Bench is the packer's whole day, so for a packer it comes first. Everyone else keeps
 * the section order as written.
 */
function orderFor(role: Role, sections: NavSection[]): NavSection[] {
  if (role !== 'packer') return sections;
  const bench = sections.filter((section) => section.id === 'bench');
  return [...bench, ...sections.filter((section) => section.id !== 'bench')];
}

function badgeFor(id: string, badges: NavBadges): number | undefined {
  switch (id) {
    case 'home':
      return badges.inbox;
    case 'orders':
      return badges.toPack;
    case 'stock':
      return badges.criticalSkus;
    case 'support':
      return badges.needsReply;
    default:
      return undefined;
  }
}

export interface TabItem {
  id: string;
  label: string;
  href?: string;
  action?: 'search' | 'more';
  /** Glyph name. Decorative: the label under it is the accessible name. */
  icon: IconName;
}

/** Phone tab bar, five slots for the owner and four for the roles that need fewer. */
export function tabsFor(role: Role | null): TabItem[] {
  switch (role) {
    case 'owner':
      return [
        { id: 'home', label: 'Home', href: '/', icon: 'home' },
        { id: 'orders', label: 'Orders', href: '/orders', icon: 'orders' },
        { id: 'stock', label: 'Stock', href: '/stock', icon: 'stock' },
        { id: 'search', label: 'Search', action: 'search', icon: 'search' },
        { id: 'more', label: 'More', action: 'more', icon: 'chevron' },
      ];
    case 'agent':
      return [
        { id: 'home', label: 'Home', href: '/', icon: 'home' },
        { id: 'support', label: 'Support', href: '/support', icon: 'support' },
        { id: 'customers', label: 'Customers', href: '/customers', icon: 'customers' },
        { id: 'search', label: 'Search', action: 'search', icon: 'search' },
      ];
    case 'packer':
      return [
        { id: 'bench', label: 'Bench', href: '/bench', icon: 'bench' },
        { id: 'home', label: 'Home', href: '/', icon: 'home' },
        { id: 'orders', label: 'Orders', href: '/orders', icon: 'orders' },
        { id: 'stock', label: 'Stock', href: '/stock', icon: 'stock' },
        { id: 'search', label: 'Search', action: 'search', icon: 'search' },
      ];
    default:
      return [{ id: 'home', label: 'Home', href: '/', icon: 'home' }];
  }
}

export interface NavSegment {
  id: string;
  label: string;
  href: string;
  /**
   * The permission that makes this segment worth showing. Absent means anyone who can reach
   * the section. Pricing is money and nothing else: a packer can call /supplier-prices and
   * get a money-free page back, which is an empty table, so the tab is not offered to her.
   */
  permission?: Permission;
}

/** Sub-nav segments, maximum six per section. DESIGN.md section 3. */
export const SEGMENTS: Readonly<Record<string, readonly NavSegment[]>> = {
  stock: [
    { id: 'overview', label: 'Overview', href: '/stock' },
    { id: 'inbound', label: 'Inbound', href: '/stock/inbound' },
    { id: 'suppliers', label: 'Suppliers', href: '/stock/suppliers' },
    { id: 'pricing', label: 'Pricing', href: '/stock/pricing', permission: 'supplier_prices.read' },
    { id: 'legend', label: 'Legend', href: '/legend' },
  ],
  support: [
    { id: 'queue', label: 'Queue', href: '/support' },
    { id: 'handoffs', label: 'Handoffs', href: '/support/handoffs' },
    { id: 'macros', label: 'Macros', href: '/support/macros' },
  ],
  finance: [
    { id: 'overview', label: 'Overview', href: '/finance' },
    { id: 'forecast', label: 'Forecast', href: '/finance/forecast' },
    { id: 'ledgers', label: 'Ledgers', href: '/finance/ledgers' },
    { id: 'pnl', label: 'P and L', href: '/finance/pnl' },
  ],
  growth: [
    { id: 'acquisition', label: 'Acquisition', href: '/growth' },
    { id: 'channels', label: 'Channels', href: '/growth/channels' },
    { id: 'email', label: 'Email', href: '/growth/email' },
    { id: 'referrals', label: 'Referrals', href: '/growth/referrals' },
    { id: 'partners', label: 'Partners', href: '/growth/partners' },
  ],
  settings: [
    { id: 'controls', label: 'Controls', href: '/settings' },
    { id: 'users', label: 'Users', href: '/settings/users' },
    { id: 'alerts', label: 'Alerts', href: '/settings/alerts' },
    { id: 'system', label: 'System', href: '/settings/system' },
    { id: 'audit', label: 'Audit', href: '/settings/audit' },
    { id: 'changelog', label: 'Changelog', href: '/settings/changelog' },
  ],
};

/** The segments of a section this set of permissions may see. No set means every segment. */
export function segmentsFor(
  sectionId: string,
  permissions?: ReadonlySet<Permission>,
): readonly NavSegment[] {
  const segments = SEGMENTS[sectionId] ?? [];
  if (!permissions) return segments;
  return segments.filter((segment) => !segment.permission || permissions.has(segment.permission));
}
